import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { selectSelectedOrganization, updateOrganization } from '../../redux/reducers/organizationReducer';
import DocumentLink from '../DocumentLink';

function EditButtons({ editMode, handleCancel, handleEditClick, handleSave, handleModalOpen }) {
  const { t } = useTranslation();

  if (editMode) {
    return (
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Button id='save' variant="contained" endIcon={<SaveIcon />} onClick={() => handleSave()}>
          {t('organization.buttonSaveEdit')}
        </Button>
        <Button id='cancel' variant="contained" endIcon={<CloseIcon />} onClick={() => handleCancel()}>
          {t('organization.buttonCancel')}
        </Button>
        <Button onClick={handleModalOpen} />
      </Stack>
    );
  }
  return (
    <Button id='edit-information' variant="contained" endIcon={<EditIcon />} onClick={() => handleEditClick()}>
      {t('organization.editButton2')}
    </Button>
  );
}

function OrganizationDetails({ title, value, editMode, handleChange, name }) {
  return (
    <Stack
      gap="10px"
      variant="organizationInfo"
    >
      <Typography variant="bold">{title}</Typography>
      {editMode
        ? (
          <TextField
            id={name}
            name={name}
            variant="outlined"
            value={value}
            multiline
            rows={2}
            onChange={handleChange}
          />
        ) : (
          <Typography
            id={value}
            variant="body1"
            sx={{
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '500px',
            }}
          >
            {value}
          </Typography>
        )}
    </Stack>
  );
}

function OrganizationInfo() {
  const [editMode, seteditMode] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const selectedOrganization = useSelector(selectSelectedOrganization);

  const [orgDetails, setOrgDetails] = useState({ ...selectedOrganization });

  const handleInitialize = () => {
    setOrgDetails({ ...selectedOrganization });
  };

  const ensureAbsoluteUrl = (documentObject) => {
    if (!documentObject.link.startsWith('https://') && !documentObject.link.startsWith('http://')) {
      documentObject.link = 'https://' + documentObject.link;
    }
    return documentObject;
  };

  const handleNewDocument = () => {
    const newState = { ...orgDetails };
    const newDocument = {
      id: uuidv4(),
      documentName: '',
      link: '',
    };
    newState.documentation = [...newState.documentation, newDocument];
    setOrgDetails(newState);
  };

  const handleUpdateDocument = (documentData) => {
    const newState = { ...orgDetails };
    const newList = [...orgDetails.documentation];
    const index = newList.findIndex((x) => x.id === documentData.id);
    newList[index] = documentData;
    const ensuredUrls = ensureAbsoluteUrl(newList[index]);
    newList[index] = ensuredUrls;
    newState.documentation = newList;
    setOrgDetails(newState);
  };

  const handleDeleteDocument = (id) => {
    const newDetails = { ...orgDetails };
    newDetails.documentation = newDetails.documentation.filter((x) => x.id !== id);
    setOrgDetails(newDetails);
  };

  const handleNewStrategy = () => {
    const newState = { ...orgDetails };
    const newStrat = {
      id: uuidv4(),
      documentName: '',
      link: '',
    };
    newState.strategyLinks = [...newState.strategyLinks, newStrat];
    setOrgDetails(newState);
  };

  const handleUpdateStrategy = (documentData) => {
    const newState = { ...orgDetails };
    const newList = [...orgDetails.strategyLinks];
    const index = newList.findIndex((x) => x.id === documentData.id);
    newList[index] = documentData;
    const ensuredUrls = ensureAbsoluteUrl(newList[index]);
    newList[index] = ensuredUrls;
    newState.strategyLinks = newList;
    setOrgDetails(newState);
  };

  const handleDeleteStrategy = (id) => {
    const newDetails = { ...orgDetails };
    newDetails.strategyLinks = newDetails.strategyLinks.filter((x) => x.id !== id);
    setOrgDetails(newDetails);
  };

  const handleSave = () => {
    dispatch(updateOrganization(orgDetails));
    seteditMode(false);
  };

  const handleEditClick = () => {
    seteditMode(!editMode);
  };

  const handleCancel = () => {
    seteditMode(false);
    handleInitialize();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const newState = { ...orgDetails };
    newState[name] = value;
    setOrgDetails(newState);
  };

  useEffect(() => {
    handleInitialize();
  }, []);

  return (
    <Box>
      <Stack
        height="80vh"
      >
        <Stack direction="column" spacing={5} alignItems="flex-start" marginTop={5}>
          <Typography variant="header">{t('organization.infoTitle')}</Typography>
          <EditButtons
            editMode={editMode}
            handleEditClick={handleEditClick}
            handleCancel={handleCancel}
            handleSave={handleSave}
          />
        </Stack>
        <Stack direction="column" gap="25px" marginTop={5}>
          <Stack direction="row" gap="250px">
            <OrganizationDetails
              title={t('organization.name')}
              name="name"
              value={orgDetails.name}
              editMode={editMode}
              handleChange={handleChange}
            />
            <OrganizationDetails
              title={t('organization.description')}
              name="description"
              value={orgDetails.description}
              editMode={editMode}
              handleChange={handleChange}
            />
          </Stack>
          <Stack direction="row" gap="250px">
            <OrganizationDetails
              title={t('organization.domain')}
              name="domain"
              value={orgDetails.domain}
              editMode={editMode}
              handleChange={handleChange}
            />
            <OrganizationDetails
              title={t('organization.personnelTitle')}
              name="personnel"
              value={orgDetails.personnel}
              editMode={editMode}
              handleChange={handleChange}
            />
          </Stack>
          <Stack direction="row" gap="250px">
            <OrganizationDetails
              title={t('organization.strategyTitle')}
              name="strategyDesc"
              value={orgDetails.strategyDesc}
              editMode={editMode}
              handleChange={handleChange}
            />
            <OrganizationDetails
              title={t('organization.functionTitle')}
              name="function"
              value={orgDetails.function}
              editMode={editMode}
              handleChange={handleChange}
            />
          </Stack>
          <Stack direction="row" gap="250px">
            <OrganizationDetails
              title={t('organization.addressTitle')}
              name="address"
              value={orgDetails.address}
              editMode={editMode}
              handleChange={handleChange}
            />
            <OrganizationDetails
              title={t('organization.serviceTitle')}
              name="service"
              value={orgDetails.service}
              editMode={editMode}
              handleChange={handleChange}
            />
          </Stack>
          <Stack direction="row" gap="250px">
            <OrganizationDetails
              title={t('organization.industryTitle')}
              name="industry"
              value={orgDetails.industry}
              editMode={editMode}
              handleChange={handleChange}
            />
            <Stack variant="organizationInfo" />
          </Stack>
          <Stack direction="row" gap="250px">
            {editMode ? (
              <Stack variant="organizationInfo">
                <Typography variant="bold">{t('organization.documentationTitle')}</Typography>
                {orgDetails.documentation.map((link) => (
                  <DocumentLink
                    key={link.id}
                    document={link}
                    handleUpdateDocument={handleUpdateDocument}
                    handleDeleteDocument={handleDeleteDocument}
                  />
                ))}
                <Button variant="outlined" onClick={handleNewDocument} endIcon={<AddIcon />}>
                  {t('organization.addLink')}
                </Button>
              </Stack>
            ) : (
              <Stack variant="organizationInfo">
                <Typography variant="bold">{t('organization.documentationTitle')}</Typography>
                {orgDetails.documentation.map((doc) => (
                  <Stack key={doc.id}>
                    <Typography variant="regular">{doc.documentName}</Typography>
                    <a href={doc.link} target="_blank" rel="noopener noreferrer">{doc.link}</a>
                  </Stack>
                ))}
              </Stack>
            )}
            {editMode ? (
              <Stack variant="organizationInfo">
                <Typography variant="bold">{t('organization.strategyLinkTitle')}</Typography>
                {orgDetails.strategyLinks.map((link) => (
                  <DocumentLink
                    key={link.documentName}
                    document={link}
                    handleUpdateDocument={handleUpdateStrategy}
                    handleDeleteDocument={handleDeleteStrategy}
                  />
                ))}
                <Button variant="outlined" onClick={handleNewStrategy} endIcon={<AddIcon />}>
                  {t('organization.addStrategyLink')}
                </Button>
              </Stack>
            ) : (
              <Stack variant="organizationInfo">
                <Typography variant="bold">{t('organization.strategyLinkTitle')}</Typography>
                {orgDetails.strategyLinks.map((doc) => (
                  <Stack key={doc.id}>
                    <Typography variant="regular">{doc.documentName}</Typography>
                    <a href={doc.link} target="_blank" rel="noopener noreferrer">{doc.link}</a>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}

export default OrganizationInfo;
